import type {DesignModuleSpecJson} from '../data/contracts';
import {recordBindingAudit,type BindingAudit} from './bindingReviewPolicy';
import {canonicalDecisionValue} from './decisionJournal';
type Json=Record<string,any>;
/** Idempotent upgrade of acquired bindings. Text tokens preserve spacing without changing targets. */
export function normalizeBindingSpacing(spec:DesignModuleSpecJson):DesignModuleSpecJson{
 const next=JSON.parse(JSON.stringify(spec)) as DesignModuleSpecJson,events:BindingAudit[]=[];
 const messages=(next.presentation as Json|undefined)?.templateMessages??{};
 let messagesChanged=false;
 const decisionAt=(document:DesignModuleSpecJson,entry:Json):unknown=>{
  const page=((document.pages??[]) as Json[]).find(page=>page.key===entry.page);
  let value:unknown=entry.nature.startsWith('node.')?page?.screen?.blocks?.find((block:Json)=>block.template)?.template?.root:page;
  for(const key of entry.path??[])value=value&&typeof value==='object'?(value as Json)[key]:undefined;
  return value;
 };
 const entries=((next.decisionJournal as Json|undefined)?.entries??[]) as Json[];
 const active=new Set(entries.filter(entry=>!entry.remove&&entry.value!==undefined&&(entry.nature.startsWith('node.')||entry.nature.startsWith('page.'))&&canonicalDecisionValue(decisionAt(spec,entry))===canonicalDecisionValue(entry.value)));
 const text=(node:Json):string=>node.type==='text'?node.token??messages[node.i18n]?.fr??'':node.type==='el'?(node.children??[]).map(text).join(''):'';
 const blank=(node:Json)=>node.type==='text'&&/^\s*$/.test(text(node));
 const endsInBinding=(node:Json):boolean=>{
  if(['field','aggregate','calculation','ratio'].includes(node.type))return true;
  if(node.type!=='el')return false;
  let index=node.children.length-1;while(index>=0&&blank(node.children[index]))index--;
  return index>=0&&endsInBinding(node.children[index]);
 };
 const trimEdge=(node:Json):boolean=>{
  if(node.type!=='el')return false;
  let changed=false;while(node.children.length&&blank(node.children[node.children.length-1])){node.children.pop();changed=true;}
  return node.children.length?trimEdge(node.children[node.children.length-1])||changed:changed;
 };
 const firstText=(node:Json):Json|undefined=>node.type==='text'?node:node.type==='el'?node.children.map(firstText).find(Boolean):undefined;
 const walk=(node:Json,path:(string|number)[],changed:(path:(string|number)[])=>void):void=>{
   if(node.type==='el'){
    for(let index=1;index<node.children.length;index++){
     const previous=node.children[index-1],unit=text(node.children[index]);
     if(endsInBinding(previous)&&/^\s*(?:CHF|EUR|USD|GBP|€|\$)(?:\b|\s|\/|$)/.test(unit)){
      // One canonical edge, kept on the unit. Preserve every binding and wrapper;
      // only redundant trailing whitespace tokens are moved across the edge.
      let updated=trimEdge(previous);
      const unitNode=firstText(node.children[index]);
      if(!/^\s/.test(unit)){
       if(!unitNode)continue;
       if(unitNode.i18n){
        const key=unitNode.i18n+'.edge-spacing';
        messages[key]??=Object.fromEntries(Object.entries(messages[unitNode.i18n]??{}).map(([locale,value])=>[locale,/* GAP-40 : une langue non traduite reste vide (repli sur le français), jamais une espace seule. */String(value??'')?' '+String(value):'']));
        unitNode.i18n=key;messagesChanged=true;
       }else unitNode.token=' '+String(unitNode.token??'');
       updated=true;
      }
      if(updated)changed([...path,'children',index-1]);
     }
    }
    node.children.forEach((child:Json,index:number)=>walk(child,[...path,'children',index],changed));
   }else if(node.type==='repeat'){walk(node.item,[...path,'item'],changed);if(node.empty)walk(node.empty,[...path,'empty'],changed);}
   else if(node.type==='calculator-template'&&node.root)walk(node.root,[...path,'root'],changed);
   else if(node.type==='block'&&node.block?.template?.root)walk(node.block.template.root,[...path,'block','template','root'],changed);
 };
 for(const page of (next.pages??[]) as Json[])for(const block of page.screen?.blocks??[]){
  if(block.template?.root)walk(block.template.root,[],path=>events.push({page:page.key,block:block.key,path,kind:'normalization',cause:'keepEdgeSpacing'}));
 }
 // Replayed render values use the same representation under their existing
 // decision identity. Never change a source origin, key, fingerprint or actor.
 const normalizeValue=(value:unknown):void=>{
  if(!value||typeof value!=='object')return;
  const node=value as Json;
  if(['el','repeat','calculator-template','block'].includes(node.type)){walk(node,[],()=>undefined);return;}
  Object.values(node).forEach(normalizeValue);
 };
 for(const entry of entries)if(entry.nature.startsWith('node.')||entry.nature.startsWith('page.')){
  const current=active.has(entry)?decisionAt(next,entry):undefined;
  if(current!==undefined)entry.value=JSON.parse(JSON.stringify(current));
  normalizeValue(entry.value);
 }
 if(messagesChanged)next.presentation={...(next.presentation as Json|undefined),templateMessages:messages};
 recordBindingAudit(next,events);return next;
}
